import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const WalletContext = createContext(null);

export const useWallet = () => {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWallet must be used within WalletProvider");
  return ctx;
};

const STORAGE_KEY = "pokerealms_wallet";

export const WalletProvider = ({ children }) => {
  const [address, setAddress] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Restore from localStorage and try silent reconnect
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setAddress(saved);

    const provider = getProvider();
    if (provider && saved) {
      provider
        .connect({ onlyIfTrusted: true })
        .then((resp) => {
          const pk = resp?.publicKey?.toString?.();
          if (pk) {
            setAddress(pk);
            localStorage.setItem(STORAGE_KEY, pk);
          }
        })
        .catch(() => {
          // silent reconnect failed; keep saved value or clear if mismatched
        });
    }

    if (provider) {
      const handleAccountChanged = (publicKey) => {
        if (publicKey) {
          const pk = publicKey.toString();
          setAddress(pk);
          localStorage.setItem(STORAGE_KEY, pk);
        } else {
          setAddress(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      };
      provider.on?.("accountChanged", handleAccountChanged);
      provider.on?.("disconnect", () => {
        setAddress(null);
        localStorage.removeItem(STORAGE_KEY);
      });
    }
  }, []);

  const getProvider = () => {
    if (typeof window === "undefined") return null;
    // Phantom exposes both window.phantom?.solana and window.solana
    if (window.phantom?.solana?.isPhantom) return window.phantom.solana;
    if (window.solana?.isPhantom) return window.solana;
    return null;
  };

  const connect = useCallback(async () => {
    setError(null);
    const provider = getProvider();
    if (!provider) {
      setError("Phantom wallet not detected. Install it from phantom.com");
      window.open("https://phantom.com/", "_blank");
      return null;
    }
    try {
      setConnecting(true);
      const resp = await provider.connect();
      const pk = resp?.publicKey?.toString?.();
      if (pk) {
        setAddress(pk);
        localStorage.setItem(STORAGE_KEY, pk);
        return pk;
      }
      return null;
    } catch (e) {
      setError(e?.message || "Failed to connect wallet");
      return null;
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    const provider = getProvider();
    try {
      await provider?.disconnect?.();
    } catch {
      // ignore
    }
    setAddress(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const shortAddress = address
    ? `${address.slice(0, 4)}…${address.slice(-4)}`
    : null;

  return (
    <WalletContext.Provider
      value={{ address, shortAddress, connect, disconnect, connecting, error, isConnected: !!address }}
    >
      {children}
    </WalletContext.Provider>
  );
};
